import { useTranslationByKey } from './use-translation-by-key';
import { useCallback, useState } from 'react';
import { ActivityLogHistoryDto } from 'shared/interfaces/user/my-rewards/activity-log-history.dto';
import { RewardHistoryDto } from 'shared/interfaces/user/my-rewards/reward-history.dto';
import { WithdrawalOptionType } from 'src/components/CustomOverlay/RewardSelection/withdrawal-option-type.enum';
import { WithdrawalProvider } from 'src/components/CustomOverlay/RewardSelection/withdrawal-provider.enum';
import { MyRewardsTabType } from 'src/components/MyRewards/MyRewardsTabs/my-rewards-tab-type.enum';
import { RewardWithdrawalContextType } from 'src/contexts/RewardWithdrawalContext/reward-withdrawal-context.type';

/**
 * Custom hook for managing reward withdrawal state and actions.
 * Provides functions to manage reward withdrawal modal states.
 */
export function useRewardWithdrawalDetails() {
  const [isAdjustRewardAmountOpen, setIsAdjustRewardAmountOpen] = useState(false);
  const [isReadyToRedeemOpen, setIsReadyToRedeemOpen] = useState(false);
  const [isSuccessfulWithdrawalOpen, setIsSuccessfulWithdrawalOpen] = useState(false);
  const [rewardSelectionOpen, setRewardSelectionOpen] = useState(false);
  const [activityLog, setActivityLog] = useState<ActivityLogHistoryDto | undefined>(undefined);
  const [rewardHistory, setRewardHistory] = useState<RewardHistoryDto | undefined>(undefined);
  const [rewardSelectionSelectedRewardId, setRewardSelectionSelectedRewardId] = useState<number | undefined>(undefined);
  const [selectedTab, setSelectedTab] = useState<MyRewardsTabType>(MyRewardsTabType.RewardHistory);
  const [selectedRewardDetailsWithdrawalId, setSelectedRewardDetailsWithdrawalId] = useState<number | undefined>(
    undefined
  );

  // PayPal withdrawal state
  const [paypalEmail, setPaypalEmail] = useState<string | undefined>(undefined);
  const [selectedProvider, setSelectedProvider] = useState<WithdrawalProvider | undefined>(undefined);
  const [isPayPalEmailInputOpen, setIsPayPalEmailInputOpen] = useState(false);

  const t = useTranslationByKey('CONTEXTS.REWARD_WITHDRAWAL');

  const resolveRewardTypeName = useCallback(
    (type: WithdrawalOptionType) => {
      switch (type) {
        case WithdrawalOptionType.MerchantCard:
          return t('GIFT_CARD');
        case WithdrawalOptionType.VisaCard:
          return t('CASH_CARD');
        case WithdrawalOptionType.Charity:
          return t('CHARITY');
        case WithdrawalOptionType.Paypal:
          return 'PayPal';
        case WithdrawalOptionType.Venmo:
          return 'Venmo';
        default:
          return '';
      }
    },
    [t]
  );

  const rewardWithdrawalState: RewardWithdrawalContextType = {
    isAdjustRewardAmountOpen,
    setIsAdjustRewardAmountOpen,
    isReadyToRedeemOpen,
    setIsReadyToRedeemOpen,
    isSuccessfulWithdrawalOpen,
    setIsSuccessfulWithdrawalOpen,
    rewardSelectionOpen,
    setRewardSelectionOpen,
    activityLog,
    setActivityLog,
    rewardHistory,
    setRewardHistory,
    rewardSelectionSelectedRewardId,
    setRewardSelectionSelectedRewardId,
    selectedTab,
    setSelectedTab,
    selectedRewardDetailsWithdrawalId,
    setSelectedRewardDetailsWithdrawalId,
    resolveRewardTypeName,
    // PayPal withdrawal support
    paypalEmail,
    setPaypalEmail,
    selectedProvider,
    setSelectedProvider,
    isPayPalEmailInputOpen,
    setIsPayPalEmailInputOpen,
  };

  return rewardWithdrawalState;
}
